import { useMutation } from '@apollo/client';
import React, { useContext, useState } from 'react';
import { ActionButton, DangerButton, SuccessButton } from '../../components/connectedForm/Button/Button';
import { IWorkspaceAction } from '../../@types/workspaceTypes';
import PurchasePaymentExport from '../../graphql/mutations/supplier/PurchasePaymentExport.graphql';
import { useComponentQueryErrorHandling } from '../../utils/parseErrorResponse';
import { useBackgroundProcess } from '../../utils/BackgroundJobs';
import getClientMutationId from '../../utils/getClientMutationId';
import CheckPopUpBlockerFunction from '../../utils/CheckPopUpBlockerFunction';
import { useDispatch } from 'react-redux';
import ModalContext from '../../components/Context/ModalContext';
import { BlurTextField } from '../../components/connectedForm/inputComponents';
import { ModalBottomButtonContainer } from '../../components/modals/Styles';
import { BasicHeader } from '../BasicHeader';
import DateField from '../../components/connectedForm/DateInput/DateInput';
import { useActionCriteria } from '../../utils/useActionProps';
import FluentModal from '../../components/modals/FluentModal';

type ExportActionModal = {
  open: boolean;
  onConfirm: (reference: string, adHocPaymentDate: string) => void;
};

const ExportActionModal = ({ open, onConfirm }: ExportActionModal) => {
  const { onClose: onModalClose } = useContext(ModalContext);
  const [reference, setReference] = useState('');
  const [adHocPaymentDate, setAdHocPaymentDate] = useState<string>('');

  return (
    <FluentModal isOpen={open} styles={{ main: { minWidth: '700px', paddingBottom: '60px' } }}>
      <BasicHeader>Export Payments</BasicHeader>
      <div style={{ padding: '2em' }}>
        <BlurTextField
          label="Reference"
          value={reference}
          onChange={setReference}
          enableOnChange
          data-testid={'purchase-payment-reference'}
        />
        <DateField
          label="Payment Date"
          value={adHocPaymentDate}
          onChange={setAdHocPaymentDate}
          hint={'This field only applies when exporting using Lloyds or Natwest "Ad Hoc Bulk Payments" formats.'}
          data-testid={'purchase-payment-export-modal-date'}
        />
        <ModalBottomButtonContainer>
          <SuccessButton onClick={() => onConfirm(reference, adHocPaymentDate)}>Confirm</SuccessButton>
          <DangerButton onClick={onModalClose}>Cancel</DangerButton>
        </ModalBottomButtonContainer>
      </div>
    </FluentModal>
  );
};

const ExportAction = () => {
  const handleQueryError = useComponentQueryErrorHandling();
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);

  const actionProps = useActionCriteria();

  const [exportPayments] = useMutation(PurchasePaymentExport, {
    onError: handleQueryError,
  });

  // @ts-ignore - still JS
  const runBackgroundProcess = useBackgroundProcess({
    title: 'Exporting',
    onComplete: (job) => {
      CheckPopUpBlockerFunction({ dispatch }, `/api/resources/temporary/${job.properties.reportFile}`, '_blank');
    },
  });

  const onConfirm = (reference: string, adHocPaymentDate: string) => {
    setModalOpen(false);

    exportPayments({
      variables: {
        input: {
          clientMutationId: getClientMutationId(),
          actionCriteria: actionProps,
          reference,
          adHocPaymentDate: adHocPaymentDate || null,
        },
      },
    }).then((response) => {
      const key = Object.keys(response.data)[0];
      runBackgroundProcess(response.data[key].result);
    });
  };

  return (
    <>
      <ActionButton label="Export" onClick={() => setModalOpen(true)} />
      <ModalContext.Provider
        value={{
          onClose: () => {
            setModalOpen(false);
          },
        }}
      >
        <ExportActionModal open={modalOpen} onConfirm={onConfirm} />
      </ModalContext.Provider>
    </>
  );
};

export const actions: Array<IWorkspaceAction> = [
  {
    actionReference: 'PURCHASE_PAYMENT_EXPORT',
    element: <ExportAction />,
    condition: (actionEntities, { todo: boolean }, type) => {
      return type === 'WORKSPACE';
    },
  },
];
